import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Clock,
  Phone,
  Mail,
  CheckCircle2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { mockData } from '../data/mock';
import ConsultationModal from '../components/ConsultationModal';
import WhatsAppButton from '../components/WhatsAppButton';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Find the project by ID
  const project = mockData.projects.find(p => p.id === parseInt(id));

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[rgb(0,55,32)] mb-4">Project Not Found</h1>
          <Button onClick={() => navigate('/')} className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const images = project.images || [project.image];
  const currentImage = images[selectedImageIndex];

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-[rgba(0,0,0,0.1)] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="flex items-center gap-2 text-[rgb(0,55,32)] hover:text-[var(--accent-primary)]"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Projects</span>
            </Button>
            <Button 
              onClick={() => setIsConsultationOpen(true)}
              className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold rounded-full h-10 px-6 hover:scale-102 transition-transform"
            >
              Book Consultation
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Project Header */}
          <div className="mb-12">
            <div className="inline-block px-4 py-1 bg-[var(--accent-wash)] text-[var(--accent-text)] rounded-full text-sm font-semibold mb-4">
              {project.category}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-[rgb(0,55,32)] mb-6">
              {project.title}
            </h1>
            <div className="flex flex-wrap gap-6 text-[rgb(131,146,140)]">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[var(--accent-primary)]" />
                <span className="font-medium">{project.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[var(--accent-primary)]" />
                <span className="font-medium">{project.duration}</span>
              </div>
            </div>
          </div>

          {/* Main Image Gallery */}
          <div className="mb-12">
            <Card className="overflow-hidden rounded-2xl border-[rgba(0,0,0,0.1)] bg-white">
              {/* Large Image Display */}
              <div className="relative aspect-video bg-[rgba(0,0,0,0.05)]">
                <img
                  src={currentImage}
                  alt={`${project.title} - Image ${selectedImageIndex + 1}`}
                  className="w-full h-full object-cover"
                  style={{
                    imageRendering: 'crisp-edges',
                    WebkitImageRendering: 'crisp-edges'
                  }}
                  loading="lazy"
                />
                
                {/* Navigation Arrows - Only show if multiple images */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg"
                    >
                      <ChevronLeft className="w-6 h-6 text-[rgb(0,55,32)]" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg"
                    >
                      <ChevronRight className="w-6 h-6 text-[rgb(0,55,32)]" />
                    </button>
                    
                    {/* Image Counter */}
                    <div className="absolute bottom-4 right-4 px-4 py-2 bg-black/70 backdrop-blur rounded-full text-white text-sm font-semibold">
                      {selectedImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnail Gallery - Only show if multiple images */}
              {images.length > 1 && (
                <div className="p-4 bg-[rgba(0,0,0,0.02)]">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`aspect-video rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                          selectedImageIndex === index 
                            ? 'border-[var(--accent-primary)] shadow-lg' 
                            : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                          style={{
                            imageRendering: 'crisp-edges',
                            WebkitImageRendering: 'crisp-edges'
                          }}
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Project Details Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Project Overview */}
            <Card className="p-8 bg-white border-[rgba(0,0,0,0.1)] rounded-xl">
              <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-6">Project Overview</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[rgb(0,55,32)] mb-1">Category</h3>
                    <p className="text-[rgb(131,146,140)]">{project.category}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[rgb(0,55,32)] mb-1">Location</h3>
                    <p className="text-[rgb(131,146,140)]">{project.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[rgb(0,55,32)] mb-1">Duration</h3>
                    <p className="text-[rgb(131,146,140)]">{project.duration}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[rgb(0,55,32)] mb-1">Total Photos</h3>
                    <p className="text-[rgb(131,146,140)]">{images.length} professional photos</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Contact CTA */}
            <Card className="p-8 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready for Your Own Project?</h2>
              <p className="mb-6 text-white/90 leading-relaxed">
                Let's discuss your renovation goals. Get a free consultation and detailed quote for your project.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => setIsConsultationOpen(true)}
                  className="w-full bg-white text-[var(--accent-primary)] font-semibold h-12 rounded-full hover:scale-102 transition-transform shadow-lg"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Free Consultation
                </Button>
                <a
                  href={`tel:${mockData.company.phone}`}
                  className="flex items-center justify-center gap-2 w-full bg-white/20 backdrop-blur text-white font-semibold h-12 rounded-full hover:bg-white/30 transition-all"
                >
                  <Phone className="w-5 h-5" />
                  {mockData.company.phone}
                </a>
                <a
                  href={`mailto:${mockData.company.email}`}
                  className="flex items-center justify-center gap-2 w-full bg-white/20 backdrop-blur text-white font-semibold h-12 rounded-full hover:bg-white/30 transition-all"
                >
                  <Mail className="w-5 h-5" />
                  Email Us
                </a>
              </div>
            </Card>
          </div>

          {/* Similar Projects */}
          <div>
            <h2 className="text-3xl font-bold text-[rgb(0,55,32)] mb-8">More Projects</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {mockData.projects
                .filter(p => p.id !== project.id)
                .slice(0, 3)
                .map((otherProject) => (
                  <Card
                    key={otherProject.id}
                    onClick={() => {
                      navigate(`/project/${otherProject.id}`);
                      window.scrollTo(0, 0);
                    }}
                    className="overflow-hidden bg-white border-[rgba(0,0,0,0.1)] rounded-xl hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="aspect-video bg-[rgba(0,0,0,0.05)] relative overflow-hidden">
                      <img
                        src={otherProject.image}
                        alt={otherProject.title}
                        className="w-full h-full object-cover"
                        style={{
                          imageRendering: 'crisp-edges',
                          WebkitImageRendering: 'crisp-edges'
                        }}
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3 bg-[var(--accent-primary)] text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {otherProject.duration}
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="text-sm text-[var(--accent-text)] font-semibold mb-2">
                        {otherProject.category}
                      </div>
                      <h3 className="text-xl font-bold text-[rgb(0,55,32)] mb-2">
                        {otherProject.title}
                      </h3>
                      <p className="text-[rgb(131,146,140)] text-sm">{otherProject.location}</p>
                    </div>
                  </Card>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ConsultationModal isOpen={isConsultationOpen} onClose={() => setIsConsultationOpen(false)} />
      <WhatsAppButton />
    </div>
  );
};

export default ProjectDetail;
